/*
 * Scentt global chrome:
 *   <scentt-mode-toggle>  light / dark switch (state lives on html[data-theme], saved per layout rules)
 *   <scentt-ab-rotate>    keeps the announcement bar rotating on touch screens
 *   menu drawer height    measures where the phone menu starts so it reaches the bottom of the screen
 * Self-contained: this file may run before scentt-core.js (both are deferred), so it never needs window.Scentt at load.
 */
(() => {
  const MODES = ['light', 'dark'];
  const KEY_SITE = 'scentt:mode';
  const KEY_EDITOR = 'scentt:mode-editor';
  const reduced = () => Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const valid = (v) => (MODES.includes(v) ? v : null);

  /* ---------------------------------------------------------------- light / dark mode */
  const html = document.documentElement;
  /** 'toggle' (storefront, saved in localStorage), 'editor' (theme editor, saved per tab), 'fixed' (no saving) */
  const storageMode = () => {
    const m = html.getAttribute('data-scentt-mode');
    return m === 'editor' || m === 'toggle' ? m : 'fixed';
  };
  const storageFor = (m) => {
    try {
      if (m === 'editor') return window.sessionStorage;
      if (m === 'toggle') return window.localStorage;
    } catch (e) { /* storage blocked */ }
    return null;
  };
  const keyFor = (m) => (m === 'editor' ? KEY_EDITOR : KEY_SITE);

  const readSaved = () => {
    const m = storageMode();
    const store = storageFor(m);
    if (!store) return null;
    try {
      const raw = store.getItem(keyFor(m));
      if (raw == null) return null;
      const v = valid(raw);
      if (!v) store.removeItem(keyFor(m)); // malformed value: drop it, keep the theme default
      return v;
    } catch (e) {
      return null;
    }
  };
  const save = (mode) => {
    const m = storageMode();
    const store = storageFor(m);
    if (!store) return;
    try { store.setItem(keyFor(m), mode); } catch (e) { /* quota / blocked: mode still applies for this page */ }
  };
  const current = () => valid(html.getAttribute('data-theme')) || 'dark';
  const apply = (mode, { persist = false } = {}) => {
    const v = valid(mode);
    if (!v) return;
    html.setAttribute('data-theme', v);
    if (persist) save(v);
    document.querySelectorAll('scentt-mode-toggle').forEach((el) => el.sync());
    document.dispatchEvent(new CustomEvent('scentt:mode', { detail: { mode: v } }));
  };

  class ScenttModeToggle extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;
      this.onClick = () => apply(current() === 'light' ? 'dark' : 'light', { persist: true });
      this.button.addEventListener('click', this.onClick);
      const saved = readSaved();
      if (saved && saved !== current()) html.setAttribute('data-theme', saved);
      this.sync();
    }

    disconnectedCallback() {
      if (this.button) this.button.removeEventListener('click', this.onClick);
    }

    sync() {
      if (!this.button) return;
      const mode = current();
      const label = mode === 'light' ? this.dataset.labelDark : this.dataset.labelLight;
      if (label) this.button.setAttribute('aria-label', String(label).slice(0, 120));
      this.dataset.current = mode;
    }
  }
  if (!customElements.get('scentt-mode-toggle')) customElements.define('scentt-mode-toggle', ScenttModeToggle);

  /* Same choice in every open tab (storefront only). */
  window.addEventListener('storage', (e) => {
    if (e.key !== KEY_SITE || storageMode() !== 'toggle') return;
    const v = valid(e.newValue);
    if (v && v !== current()) apply(v);
  });

  /* ---------------------------------------------------------------- announcement bar on touch screens
     Trade pauses its slideshow on mouseover/focusin and resumes on mouseleave/focusout. Touch screens never
     send mouseleave, so one tap stops the rotation for good, and some mobile browsers ignore its scroll call.
     On touch-only devices this element takes over the rotation of its parent <slideshow-component>. */
  class ScenttAbRotate extends HTMLElement {
    connectedCallback() {
      if (!window.matchMedia || !window.matchMedia('(hover: none)').matches) return;
      // Wait one tick so Trade's slideshow-component (global.js) has initialised.
      this.boot = setTimeout(() => this.start(), 300);
    }

    disconnectedCallback() {
      clearTimeout(this.boot);
      clearInterval(this.timer);
      if (this.comp && this.onHold) {
        this.comp.removeEventListener('touchstart', this.onHold);
        this.comp.removeEventListener('touchend', this.onHold);
        this.comp.removeEventListener('click', this.onHold);
      }
    }

    start() {
      const comp = this.closest('slideshow-component');
      const slider = comp && comp.querySelector('.slider');
      if (!comp || !slider) return;
      const secs = Number.parseInt(this.dataset.speed, 10);
      const speed = (Number.isFinite(secs) ? Math.min(30, Math.max(2, secs)) : 5) * 1000;
      this.comp = comp;
      this.slider = slider;
      this.holdUntil = 0;
      // Stop Trade's own timer and keep it stopped (its focus/mouse handlers would restart it).
      if (typeof comp.pause === 'function') comp.pause();
      comp.play = () => {};
      this.onHold = () => { this.holdUntil = Date.now() + speed; };
      comp.addEventListener('touchstart', this.onHold, { passive: true });
      comp.addEventListener('touchend', this.onHold, { passive: true });
      comp.addEventListener('click', this.onHold);
      this.timer = setInterval(() => this.step(), speed);
    }

    step() {
      if (Date.now() < this.holdUntil || document.hidden || !this.isConnected) return;
      const slider = this.slider;
      const items = Array.from(slider.querySelectorAll('.slider__slide')).filter((el) => el.clientWidth > 0);
      if (items.length < 2) return;
      const w = items[1].offsetLeft - items[0].offsetLeft;
      if (!w) return;
      const index = Math.round(slider.scrollLeft / w);
      const next = index + 1 >= items.length ? 0 : index + 1;
      const left = next * w;
      if (reduced() || next === 0) {
        const prev = slider.style.scrollBehavior;
        slider.style.scrollBehavior = 'auto';
        slider.scrollLeft = left;
        slider.style.scrollBehavior = prev;
      } else {
        slider.scrollTo({ left, behavior: 'smooth' });
        // Some mobile browsers drop smooth scrolls inside a scroll-snap container: make sure we arrive.
        setTimeout(() => { if (Math.abs(slider.scrollLeft - left) > 2) slider.scrollLeft = left; }, 700);
      }
    }
  }
  if (!customElements.get('scentt-ab-rotate')) customElements.define('scentt-ab-rotate', ScenttAbRotate);

  /* ---------------------------------------------------------------- phone menu height
     Trade sizes the menu from the header's bottom edge; when the drawer actually starts lower (or the address bar
     moves) a strip of the page showed underneath. Measure the drawer's real top while it is open and hand it to the
     CSS (assets/scentt-global.css, --scentt-drawer-top). Read-only DOM measurement, no user data involved. */
  const fitDrawer = () => {
    const drawer = document.querySelector('header-drawer details[open] > .menu-drawer');
    if (!drawer) return;
    const top = Math.round(drawer.getBoundingClientRect().top);
    if (Number.isFinite(top) && top >= 0 && top < window.innerHeight) html.style.setProperty('--scentt-drawer-top', top + 'px');
  };
  document.addEventListener('toggle', (e) => {
    const t = e.target;
    if (t && t.matches && t.matches('header-drawer details') && t.open) window.requestAnimationFrame(fitDrawer);
  }, true);
  let fitTimer = 0;
  window.addEventListener('resize', () => {
    window.clearTimeout(fitTimer);
    fitTimer = window.setTimeout(fitDrawer, 120);
  }, { passive: true });
})();
